import React, { useCallback, useMemo, useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { UiPen } from '@vyaguta/vyaguta-icons/ui';

import AddLink from 'pages/eventsAndNotices/linkLibrary/components/AddLink';
import DeletePopup from 'pages/eventsAndNotices/linkLibrary/components/DeletePopup';

import LFModal from 'components/common/LFModal/LFModal';

import { useDeleteQuickLinksMutation } from 'hooks/query/useQuickLinksQuery';

import * as toast from 'utils/toast';

import { LinkItem } from 'interface/linkLibraryTree';
import type { SetHierarchyData } from 'pages/eventsAndNotices/linkLibrary/types';

interface Props {
  selectedLink: LinkItem;
  setHierarchyData?: SetHierarchyData;
  popoverId?: string;
  setActivePopoverId?: React.Dispatch<React.SetStateAction<string | null>>;
}

const LinkVerticalEllipsis: React.FC<Props> = (props) => {
  const { selectedLink, setHierarchyData, popoverId, setActivePopoverId } = props;
  const { mutateAsync: deleteQuickLinks } = useDeleteQuickLinksMutation();

  const [isLinkEditModalOpen, setIsLinkEditModalOpen] = useState(false);

  const closeLinkEditModal = () => {
    setIsLinkEditModalOpen(false);
  };

  const closePopover = useCallback(() => {
    if (!setActivePopoverId || !popoverId) {
      return;
    }

    setActivePopoverId((current) => (current === popoverId ? null : current));
  }, [popoverId, setActivePopoverId]);

  const EditLinkSubCategory = useMemo<React.FC<{ onClick: () => void }>>(
    () =>
      function EditLinkSubCategoryButton({ onClick }: { onClick: () => void }) {
        return (
          <li
            className="overflow-menu__list-item overflow-menu__list-item--hoverable d-flex"
            onClick={() => {
              closePopover();
              onClick();
              setIsLinkEditModalOpen(true);
            }}
          >
            <span className="icon overflow-menu__icon">
              <UiPen className="grey--text" size={16} />
            </span>
            <span>Edit</span>
          </li>
        );
      },
    [closePopover]
  );

  const DeleteLinkTrigger = useMemo<React.FC<{ onClick: () => void }>>(
    () =>
      function DeleteLinkTriggerButton({ onClick }: { onClick: () => void }) {
        return (
          <DeletePopup
            onClick={(e) => {
              e.preventDefault();
              closePopover();
              onClick();
            }}
          />
        );
      },
    [closePopover]
  );

  const deleteLink = async () => {
    const data = (await deleteQuickLinks(selectedLink.id)) as { message: string };

    toast.success({
      title: 'Success',
      message: data.message,
    });
  };

  return (
    <div className="overflow-menu">
      <div className="overflow-menu__block">
        <ul className="overflow-menu__list">
          <AddLink
            isEdit={true}
            isOpen={isLinkEditModalOpen}
            onClose={closeLinkEditModal}
            selectedLink={selectedLink}
            setHierarchyData={setHierarchyData}
            TriggerBtn={EditLinkSubCategory}
          />
          <LFModal
            TriggerBtn={DeleteLinkTrigger}
            onClose={closePopover}
            modalStatusIcon={<FiAlertTriangle color="#F44336" size={24} />}
            title={{ text: 'Do you want to delete the link?', type: 'danger' }}
            className="delete-modal"
            htmlContent={
              <div className="d-grid mbpx-4">
                <span className="delete__text">
                  This action cannot be reverted back. The {selectedLink?.name} link will be deleted.
                </span>
              </div>
            }
            renderFooter={true}
            buttons={[
              {
                text: 'Yes, delete',
                type: 'red',
                className: 'mr-12 delete__btn',
                onClick: deleteLink,
                close: true,
                showLoading: true,
              },
              {
                text: 'No, go back',
                type: 'outlined-grey',
                className: 'py-8 px-12',
                close: true,
              },
            ]}
          />
        </ul>
      </div>
    </div>
  );
};

export default LinkVerticalEllipsis;
